import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgForm,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  MatFormField,
  MatLabel,
  MatHint,
  MatError,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  selector: 'dges-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoginFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
    MatError,
  ],
})
export class LoginFormComponent implements ControlValueAccessor {
  public isDisabled = false;

  // Function to call when the input is touched (when a star is clicked).
  onTouched: () => void;

  onChanged: ((value: unknown) => void) | undefined;

  writeValue(obj: unknown): void {
    this.loginForm.setValue(obj);

    this.loginForm.valueChanges.subscribe(() => {
      if (this.onChanged) this.onChanged(this.loginForm.value);
    });
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  loginForm = new UntypedFormGroup({
    emailFormControl: new UntypedFormControl(
      { value: null, disabled: this.isDisabled },
      [Validators.required, Validators.email],
    ),
    passwordFormControl: new UntypedFormControl(
      { value: null, disabled: this.isDisabled },
      [Validators.required],
    ),
  });

  get emailFormControl() {
    return this.loginForm.controls['emailFormControl'];
  }

  get passwordFormControl() {
    return this.loginForm.controls['passwordFormControl'];
  }

  matcher = new MyErrorStateMatcher();
}
